/**
 * ERP Price Import Renderer (ERP-ONBOARDING slice 10, design O12 / O13).
 *
 * The stock renderer's twin — same four buckets, same coverage statement — with `container`
 * meaning PRICE LIST instead of warehouse.
 *
 * ── Why coverage is rendered at all ──────────────────────────────────────────
 * A price sweep only ever reads MAPPED Odoo price lists. Five detected, two mapped, and the
 * run is completely correct AND covers two fifths of the seller's prices — with a fully
 * green bar, because the denominator is derived from the same scoped read. Nothing in the
 * progress bar can express that, so the row carries `mappedContainerCount` /
 * `detectedContainerCount` and this renderer reads "2 of 5 ERP price lists". A run at full
 * coverage says so too: the operator should never have to infer the difference between
 * "all of them" and "the ones you happened to map".
 *
 * ── The four buckets, and why one of them is always zero here ────────────────
 * `unresolved` is the bucket that deserves attention — ERP products with no Teselly item,
 * which the sweep deliberately did NOT mint (O10), i.e. "import your catalogue first".
 * `unchanged` is structurally 0 on price: `ApplyBulkPriceChange` has no equality damper to
 * damp against (it loads only the price-list metadata stream, never the per-row ones — the
 * check ADR-190 D16 retired), so a price re-run genuinely re-writes every row. It is
 * rendered anyway, at zero, because the three ERP kinds share one vocabulary and a bucket
 * that silently disappears on one of them is harder to read than one that honestly says 0.
 *
 * ── Read the counts through the SHARED selectors, never the raw row fields ────
 * Only `markCompleted` ever writes `successCount` / `errorCount` onto the row; an ERRORED
 * run never reaches it (`createBatchProgressAdvancer.recordError` writes only
 * `{ completedCount, erroredTargetIds, status, updatedAt }`). Reading the raw fields
 * renders 0/0/0/0 exactly when something went wrong, directly under a bar showing the real
 * number. Slice 5 shipped that bug and fixed it; this renderer inherits the fix.
 *
 * Data shape (from ErpPriceImportBatchProcessStartedEventSchema):
 *   connectorId: string
 *   integrationUserId: string
 *   mappedContainerCount: number
 *   detectedContainerCount: number
 */

import { FC } from 'react';
import { Group, Stack, Text, ThemeIcon } from '@mantine/core';
import { IconCurrencyDollar } from '@tabler/icons-react';
import { batchProcessesTestIds } from '@domains/batchProcesses/testids';
import type { ProgressProcess } from '../../../shared/state/progressSelectors';
import { renderErpImportBreakdown } from './ErpImportBreakdown';
import type { DetailRendererProps, OperationRendererConfig, ProcessData } from './index';

const DetailComponent: FC<DetailRendererProps> = ({ process, t }) => {
  const data = ( process.data || {}) as ProcessData;
  // `ProgressProcess` is the readmodel row PLUS the completion-carried counters. Every
  // added member is optional, so a plain row widens to it without a cast — and this
  // renderer reads it through the same selectors the progress bar uses, so neither can
  // quietly invent its own fallback rule.
  const counters: ProgressProcess = process;

  const mapped = Number(( data as Record<string, unknown> ).mappedContainerCount ?? 0 );
  const detected = Number(( data as Record<string, unknown> ).detectedContainerCount ?? 0 );

  return (
    <Stack gap="sm">
      <Text fz="sm" c="dimmed">
        {t(
          'batchProcesses_operations_ErpPriceImport_description',
          'Importación de los precios del ERP en cada lista de precios mapeada.'
        )}
      </Text>

      <Group gap="xs">
        <Text fz="sm" fw={500}>
          {t( 'batchProcesses_renderer_sourceErp', 'ERP de origen' )}:
        </Text>
        <ThemeIcon size="sm" variant="light" color="gray">
          <IconCurrencyDollar size={14} />
        </ThemeIcon>
        <Text fz="sm">
          {data.connectorId ?? t( 'batchProcesses_renderer_none' )}
        </Text>
      </Group>

      {/*
        * THE COVERAGE LINE (O12). Rendered whether or not the mapping is partial: a run at
        * full coverage saying "3 of 3" is what makes "1 of 3" legible when it happens, and
        * an operator must never have to infer it from a green bar.
        */}
      <Group gap="xs" data-testid={batchProcessesTestIds.erpPriceImportCoverage}>
        <Text fz="sm" fw={500}>
          {t( 'batchProcesses_renderer_erpPriceCoverage', 'Listas de precios del ERP' )}:
        </Text>
        <Text
          fz="sm"
          c={detected > mapped ? 'var(--mantine-color-orange-6)' : undefined}
        >
          {t( 'batchProcesses_renderer_erpPriceCoverageValue', {
            mapped,
            detected,
            defaultValue: `${ mapped } de ${ detected } mapeadas`
          })}
        </Text>
      </Group>

      {/* SHARED with the item and stock renderers — see `ErpImportBreakdown.tsx`. */}
      {renderErpImportBreakdown( counters, t )}
    </Stack>
  );
};

export const config: Omit<OperationRendererConfig, 'icon' | 'color' | 'labelKey'> = {
  getSummary: ( data, t ) => {
    const connectorId = data?.connectorId;
    if ( connectorId ) {
      return t( 'batchProcesses_summary_fromErp', { erp: connectorId, defaultValue: `Desde ${ connectorId }` });
    }
    return t( 'batchProcesses_summary_unknownOperation' );
  },
  DetailComponent,
  // The targets are (item, price list) pairs, and the row carries neither list — the
  // shared reference-data resolver has nothing to resolve.
  getRequiredReferenceIds: () => ({})
};

export default DetailComponent;
